import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    // On an actual variant change (never on initial page load) point the incoming slideshow at the
    // selected variant's media so the main image swaps to it. Composite-hero products (e.g. bundles)
    // open on the hero; other PDPs may map a variant to any slide index.
    const variantMediaId = event.detail.resource?.featured_media?.id;
    let targetIndex = -1;
    if (variantMediaId != null) {
      const slideshow = newMediaGallery.querySelector('slideshow-component');
      if (slideshow) {
        const slides = Array.from(slideshow.querySelectorAll('slideshow-slide'));
        targetIndex = slides.findIndex((slide) => slide.querySelector(`[data-media-id="${variantMediaId}"]`));
        if (targetIndex >= 0) slideshow.setAttribute('initial-slide', String(targetIndex));
      }
    }

    this.replaceWith(newMediaGallery);

    // Setting initial-slide alone does NOT move the main image to a non-zero slide: Slideshow's
    // #setupSlideshow pre-sets `current` to initial-slide, so Slideshow#select then early-returns on
    // its `currentSlide === requestedSlide` guard before it can scroll the viewport. The result is the
    // thumbnail highlights the variant's slide but the main image stays on slide 0. So once the
    // reconnected slideshow's scroller is ready, reset `current` and select to force the actual scroll.
    if (targetIndex > 0) {
      const slideshow = newMediaGallery.querySelector('slideshow-component');
      let attempts = 0;
      const forceSelect = () => {
        if (!slideshow || attempts++ > 60) return;
        const { scroller, slides } = slideshow.refs ?? {};
        if (!scroller || !slides?.length) {
          requestAnimationFrame(forceSelect);
          return;
        }
        slideshow.current = 0;
        slideshow.select(targetIndex, undefined, { animate: false });
        // select() early-returns (leaving `current` unchanged at 0) until the slideshow's internal
        // scroller is ready. Once it takes effect it sets current = targetIndex and starts the scroll
        // (which settles asynchronously) — so retry only while it hasn't taken yet, and never re-fire
        // afterwards, or we'd restart the in-progress scroll every frame and it would never land.
        if (slideshow.current !== targetIndex) requestAnimationFrame(forceSelect);
      };
      requestAnimationFrame(forceSelect);
    }
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
